import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { searchDepartments } from '../services/universityService';
import {
  ArrowLeftIcon,
  ChartBarIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const Goals = () => {
  const { user, setUser } = useAuth();

  const [activeTab, setActiveTab] = useState('net-goals'); // 'net-goals', 'university-goal'
  
  const [targetNets, setTargetNets] = useState({
    tyt: {
      turkish: 0,
      math: 0,
      science: 0,
      social: 0
    },
    ayt: {
      mathAyt: 0,
      physics: 0,
      chemistry: 0,
      biology: 0
    }
  });

  const [targetGoal, setTargetGoal] = useState({
    university: '',
    department: '',
    targetScore: 0,
    motivationQuote: 'Hayallerimin peşinden koşuyorum! 🚀',
    departmentId: null,
    baseScore: null,
    topScore: null,
    scoreType: null
  });

  const [departmentSearch, setDepartmentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isManualSearch, setIsManualSearch] = useState(true);
  const searchTimeoutRef = useRef(null);
  const searchBoxRef = useRef(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.targetNets) {
        setTargetNets(user.targetNets);
      }
      if (user.targetGoal) {
        // Parse all numeric values from database
        const parsedGoal = {
          ...user.targetGoal,
          targetScore: parseFloat(user.targetGoal.targetScore) || 0,
          baseScore: user.targetGoal.baseScore ? parseFloat(user.targetGoal.baseScore) : null,
          topScore: user.targetGoal.topScore ? parseFloat(user.targetGoal.topScore) : null
        };
        setTargetGoal(parsedGoal);
        // Eğer departman seçiliyse, search input'a da yaz
        if (user.targetGoal.university && user.targetGoal.department) {
          setDepartmentSearch(`${user.targetGoal.university} - ${user.targetGoal.department}`);
        }
      }
    }
  }, [user]);

  // Click outside handler for search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Department search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Eğer manuel arama değilse (seçim yapıldıysa), arama yapma
    if (!isManualSearch) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    if (departmentSearch.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('🔍 Searching for:', departmentSearch);
        console.log('👤 User targetField:', user?.targetField);
        const response = await searchDepartments(departmentSearch, {
          scoreType: user?.targetField,
          limit: 20
        });
        console.log('📦 API Response:', response);
        console.log('📚 Departments found:', response.departments?.length || 0);
        setSearchResults(response.departments || []);
        setShowResults(true);
      } catch (error) {
        console.error('❌ Search error:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [departmentSearch, user?.targetField, isManualSearch]);

  const handleDepartmentSelect = (department) => {
    setTargetGoal({
      ...targetGoal,
      university: department.university.name,
      department: department.name,
      targetScore: parseFloat(department.baseScore2025) || 0,
      departmentId: department.id,
      baseScore: parseFloat(department.baseScore2025) || 0,
      topScore: parseFloat(department.topScore2025) || 0,
      scoreType: department.scoreType
    });
    setDepartmentSearch(`${department.university.name} - ${department.name}`);
    setShowResults(false);
    setIsManualSearch(false); // Seçim yapıldı, manuel arama değil
  };

  const handleTargetNetsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/target-nets`,
        { targetNets },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update both local state and AuthContext
      const updatedUser = { ...user, targetNets };
      setUser(updatedUser);
      
      // Also update localStorage to persist
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      alert('✅ Hedef netler başarıyla kaydedildi! Dashboard\'da ilerlemenizi takip edebilirsiniz.');
    } catch (error) {
      console.error('Target nets update error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Hedef netler güncellenirken hata oluştu!';
      alert('❌ Hata: ' + errorMessage + '\n\nLütfen backend sunucusunun çalıştığından emin olun.');
    } finally {
      setLoading(false);
    }
  };

  const handleTargetGoalUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/goal`,
        { targetGoal },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update both local state and AuthContext
      const updatedUser = { ...user, targetGoal };
      setUser(updatedUser);
      
      // Also update localStorage to persist
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      alert('✅ Hedef üniversite bilgileri başarıyla kaydedildi!\n\nDashboard\'da hedef ilerlemenizi görebilirsiniz. İstediğiniz zaman buradan güncelleyebilirsiniz.');
    } catch (error) {
      console.error('Target goal update error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Hedef bilgileri güncellenirken hata oluştu!';
      alert('❌ Hata: ' + errorMessage + '\n\nLütfen tüm alanları doğru doldurduğunuzdan ve backend sunucusunun çalıştığından emin olun.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'net-goals', label: 'Net Hedeflerim', icon: ChartBarIcon },
    { id: 'university-goal', label: 'Üniversite Hedefim', icon: AcademicCapIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Mobile Responsive */}
        <div className="mb-4 sm:mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Dashboard'a Dön
          </Link>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">🎯 Hedeflerim</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Net ve üniversite hedeflerini belirle, ilerleme kaydet</p>
        </div>

        {/* Tabs - Mobile Responsive */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm mb-4 sm:mb-6">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex -mb-px min-w-max sm:min-w-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex-1 py-3 px-4 sm:py-4 sm:px-6 text-center border-b-2 font-medium text-xs sm:text-sm transition whitespace-nowrap
                      ${activeTab === tab.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-4 sm:p-6">
            {/* Net Goals Tab */}
            {activeTab === 'net-goals' && (
              <form onSubmit={handleTargetNetsUpdate} className="space-y-4 sm:space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm text-blue-800">
                    💡 <strong>İpucu:</strong> Her ders için hedef net belirleyin. Dashboard'da mevcut durumunuzu bu hedeflerle karşılaştırabilirsiniz.
                  </p>
                </div>

                {/* TYT Goals */}
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm mr-2">TYT</span>
                    Hedef Netlerim
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        📖 Türkçe Hedefi (max 40)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="40"
                        step="0.25"
                        value={targetNets.tyt.turkish}
                        onChange={(e) => setTargetNets({
                          ...targetNets,
                          tyt: { ...targetNets.tyt, turkish: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="örn: 30"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        🔢 Matematik Hedefi (max 40)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="40"
                        step="0.25"
                        value={targetNets.tyt.math}
                        onChange={(e) => setTargetNets({
                          ...targetNets,
                          tyt: { ...targetNets.tyt, math: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="örn: 35"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        🔬 Fen Bilimleri Hedefi (max 20)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        step="0.25"
                        value={targetNets.tyt.science}
                        onChange={(e) => setTargetNets({
                          ...targetNets,
                          tyt: { ...targetNets.tyt, science: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="örn: 15"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        🌍 Sosyal Bilimler Hedefi (max 20)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        step="0.25"
                        value={targetNets.tyt.social}
                        onChange={(e) => setTargetNets({
                          ...targetNets,
                          tyt: { ...targetNets.tyt, social: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="örn: 18"
                      />
                    </div>
                  </div>

                  <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-gray-600">
                      TYT Toplam Hedef: <span className="font-bold text-gray-900">
                        {(targetNets.tyt.turkish + targetNets.tyt.math + targetNets.tyt.science + targetNets.tyt.social).toFixed(2)} / 120 net
                      </span>
                    </p>
                  </div>
                </div>

                {/* AYT Goals */}
                {user?.targetField === 'SAY' && (
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <span className="bg-purple-100 text-purple-800 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm mr-2">AYT Sayısal</span>
                      Hedef Netlerim
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          🔢 Matematik Hedefi (max 40)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="40"
                          step="0.25"
                          value={targetNets.ayt.mathAyt}
                          onChange={(e) => setTargetNets({
                            ...targetNets,
                            ayt: { ...targetNets.ayt, mathAyt: parseFloat(e.target.value) || 0 }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="örn: 30"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ⚛️ Fizik Hedefi (max 14)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="14"
                          step="0.25"
                          value={targetNets.ayt.physics}
                          onChange={(e) => setTargetNets({
                            ...targetNets,
                            ayt: { ...targetNets.ayt, physics: parseFloat(e.target.value) || 0 }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="örn: 10"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          🧪 Kimya Hedefi (max 13)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="13"
                          step="0.25"
                          value={targetNets.ayt.chemistry}
                          onChange={(e) => setTargetNets({
                            ...targetNets,
                            ayt: { ...targetNets.ayt, chemistry: parseFloat(e.target.value) || 0 }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="örn: 9"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          🧬 Biyoloji Hedefi (max 13)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="13"
                          step="0.25"
                          value={targetNets.ayt.biology}
                          onChange={(e) => setTargetNets({
                            ...targetNets,
                            ayt: { ...targetNets.ayt, biology: parseFloat(e.target.value) || 0 }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          placeholder="örn: 9"
                        />
                      </div>
                    </div>

                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        AYT Toplam Hedef: <span className="font-bold text-gray-900">
                          {(targetNets.ayt.mathAyt + targetNets.ayt.physics + targetNets.ayt.chemistry + targetNets.ayt.biology).toFixed(2)} / 80 net
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-50 text-sm sm:text-base"
                  >
                    {loading ? 'Kaydediliyor...' : user?.targetNets ? '💾 Hedefleri Güncelle' : '💾 Hedefleri Kaydet'}
                  </button>
                </div>
              </form>
            )}

            {/* University Goal Tab */}
            {activeTab === 'university-goal' && (
              <form onSubmit={handleTargetGoalUpdate} className="space-y-4 sm:space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3 sm:p-4 lg:p-5 mb-4 sm:mb-6">
                  <div className="flex items-start">
                    <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mt-0.5 sm:mt-1 mr-2 sm:mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-purple-900 mb-1 sm:mb-2">🎯 Hedef Üniversite & Bölüm</h3>
                      <p className="text-xs sm:text-sm text-purple-800">
                        2025 ÖSYM verilerine göre üniversite ve bölüm seçin. 
                        Dashboard'da taban-tavan puanlarla karşılaştırma yapabilirsiniz.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Live Department Search */}
                <div ref={searchBoxRef} className="relative">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    🔍 Üniversite & Bölüm Ara *
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    <input
                      type="text"
                      value={departmentSearch}
                      onChange={(e) => {
                        setDepartmentSearch(e.target.value);
                        setIsManualSearch(true); // Kullanıcı yazıyor, manuel arama
                      }}
                      onFocus={() => {
                        if (searchResults.length > 0 && isManualSearch) {
                          setShowResults(true);
                        }
                      }}
                      className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Örn: Boğaziçi Bilgisayar, ODTÜ Makine..."
                    />
                    {searchLoading && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Search Results Dropdown - Mobile Responsive */}
                  {showResults && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 sm:max-h-96 overflow-y-auto">
                      {searchResults.map((dept) => (
                        <button
                          key={dept.id}
                          type="button"
                          onClick={() => handleDepartmentSelect(dept)}
                          className="w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-purple-50 border-b border-gray-100 last:border-b-0 transition"
                        >
                          <div className="flex items-start justify-between gap-2 sm:gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{dept.university.name}</p>
                              <p className="text-xs sm:text-sm text-gray-600 mt-1">{dept.name}</p>
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2">
                                <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-semibold rounded ${
                                  dept.scoreType === 'SAY' ? 'bg-blue-100 text-blue-800' :
                                  dept.scoreType === 'EA' ? 'bg-green-100 text-green-800' :
                                  dept.scoreType === 'SÖZ' ? 'bg-orange-100 text-orange-800' :
                                  'bg-purple-100 text-purple-800'
                                }`}>
                                  {dept.scoreType}
                                </span>
                                <span className="text-xs text-gray-500">{dept.university.city}</span>
                                <span className="text-xs text-gray-500">{dept.educationType}</span>
                                {dept.scholarshipRate && (
                                  <span className="text-xs text-green-600 font-semibold">
                                    %{dept.scholarshipRate} Burs
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs text-gray-500">Taban</p>
                              <p className="text-sm sm:text-base lg:text-lg font-bold text-purple-600">
                                {parseFloat(dept.baseScore2025 || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {showResults && searchResults.length === 0 && !searchLoading && departmentSearch.length >= 2 && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      Sonuç bulunamadı. Farklı anahtar kelimeler deneyin.
                    </div>
                  )}
                </div>

                {/* Selected Department Info - Mobile Responsive */}
                {targetGoal.university && targetGoal.department && (
                  <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-purple-900 text-base sm:text-lg mb-2">
                          📍 Seçili Hedef
                        </h4>
                        <p className="text-sm sm:text-base text-gray-900 font-semibold truncate">{targetGoal.university}</p>
                        <p className="text-xs sm:text-sm text-gray-700 mt-1">{targetGoal.department}</p>
                        {targetGoal.scoreType && (
                          <span className={`inline-block mt-2 px-3 py-1 text-xs font-bold rounded-full ${
                            targetGoal.scoreType === 'SAY' ? 'bg-blue-200 text-blue-900' :
                            targetGoal.scoreType === 'EA' ? 'bg-green-200 text-green-900' :
                            targetGoal.scoreType === 'SÖZ' ? 'bg-orange-200 text-orange-900' :
                            'bg-purple-200 text-purple-900'
                          }`}>
                            {targetGoal.scoreType}
                          </span>
                        )}
                      </div>
                      {targetGoal.baseScore && targetGoal.topScore && (
                        <div className="text-right">
                          <p className="text-xs text-gray-600 mb-1">2025 Puan Aralığı</p>
                          <div className="bg-white rounded-lg p-2 sm:p-3">
                            <p className="text-xs sm:text-sm text-gray-600">Tavan</p>
                            <p className="text-base sm:text-lg font-bold text-green-600">
                              {parseFloat(targetGoal.topScore || 0).toFixed(2)}
                            </p>
                            <div className="border-t border-gray-300 my-2"></div>
                            <p className="text-sm text-gray-600">Taban</p>
                            <p className="text-lg font-bold text-orange-600">
                              {parseFloat(targetGoal.baseScore || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    🎯 Hedef Puanınız *
                  </label>
                  <input
                    type="number"
                    required
                    min="100"
                    max="600"
                    step="0.001"
                    value={targetGoal.targetScore || ''}
                    onChange={(e) => setTargetGoal({ ...targetGoal, targetScore: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                    placeholder="Örn: 485.50"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    💡 Seçtiğiniz bölümün taban puanı: {targetGoal.baseScore ? parseFloat(targetGoal.baseScore).toFixed(3) : 'Henüz bölüm seçilmedi'}
                  </p>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    💪 Motivasyon Sözünüz
                  </label>
                  <textarea
                    value={targetGoal.motivationQuote}
                    onChange={(e) => setTargetGoal({ ...targetGoal, motivationQuote: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                    placeholder="Kendimi motive eden bir söz..."
                    rows="3"
                    maxLength="200"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    ✨ Bu söz dashboard'da görünecek ({targetGoal.motivationQuote?.length || 0}/200)
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
                  <h4 className="font-semibold text-purple-900 mb-2 text-sm sm:text-base">📋 Hedef Özeti</h4>
                  <div className="space-y-1 text-xs sm:text-sm text-purple-800">
                    <p><strong>Üniversite:</strong> {targetGoal.university || '-'}</p>
                    <p><strong>Bölüm:</strong> {targetGoal.department || '-'}</p>
                    <p><strong>Hedef Puan:</strong> {targetGoal.targetScore > 0 ? targetGoal.targetScore.toFixed(2) : '-'}</p>
                    {targetGoal.baseScore && targetGoal.topScore && (
                      <p><strong>Bölüm Puan Aralığı:</strong> {parseFloat(targetGoal.baseScore).toFixed(2)} - {parseFloat(targetGoal.topScore).toFixed(2)}</p>
                    )}
                    <p><strong>Motivasyon:</strong> "{targetGoal.motivationQuote}"</p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading || !targetGoal.university || !targetGoal.department}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {loading ? 'Kaydediliyor...' : user?.targetGoal?.university ? '💾 Hedefi Güncelle' : '💾 Hedefi Kaydet'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Goals;
